import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Plus,
  Plug,
  CheckCircle2,
  XCircle,
  Trash2,
  Pencil,
  Save,
  X,
  Loader2,
  AlertCircle,
  Star,
  Database,
  UserCheck,
} from 'lucide-react';
import { getApiBase } from './apiBase';
import ConnectionHealthPanel from './AiCopilot/ConnectionHealthPanel';

const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 60000,
});

const EMPTY_DRAFT = {
  name: '',
  description: '',
  baseUrl: '',
  username: '',
  password: '',
  rejectUnauthorized: true,
  aiSqlRunnerReportPath: '',
  aiSqlRunnerQueryParam: '',
  aiSqlRunnerSqlEncoding: '',
  fndAuditReportPath: '',
  fndAuditQueryParam: '',
  fndAuditSqlEncoding: '',
  auditPollSource: 'hcm_sensitive',
  fusionDbEnabled: false,
  fusionDbUser: '',
  fusionDbPassword: '',
  fusionDbConnectString: '',
  fusionDbWalletPath: '',
  environmentLabel: '',
};

function StatusPill({ status, message }) {
  if (!status) return null;
  const ok = status === 200;
  const cls = ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200';
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}
      title={message || ''}
    >
      <Icon className="h-3 w-3" />
      {ok ? 'HTTP 200' : `HTTP ${status}`}
    </span>
  );
}

function isAuthRequired(res) {
  return res?.status === 401 && (res?.data?.code === 'AUTH_REQUIRED' || res?.data?.error === 'Not logged in');
}

export default function Connections({ onActiveChanged, onSessionLost }) {
  const [connections, setConnections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [mongoMissing, setMongoMissing] = useState(false);
  const [mongoDetail, setMongoDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [draftId, setDraftId] = useState(null); // null = no draft, 'new' = new row, '<id>' = edit
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [savingDraft, setSavingDraft] = useState(false);

  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState({}); // id → result
  const [draftEmpsCheck, setDraftEmpsCheck] = useState(null);
  const [checkDraftEmpsBusy, setCheckDraftEmpsBusy] = useState(false);

  async function checkDraftEmps() {
    setDraftEmpsCheck(null);
    if (draftId === 'new') {
      if (!draft.baseUrl || !draft.username || !draft.password) {
        setError('Enter URL, username, and password to run the emps/1 credential check.');
        return;
      }
    } else if (!draft.baseUrl || !draft.username) {
      setError('Enter URL and username.');
      return;
    }
    setCheckDraftEmpsBusy(true);
    setError('');
    try {
      let res;
      if (draftId === 'new' || draft.password) {
        res = await API.post(
          '/api/hcm/check-credentials',
          {
            baseUrl: draft.baseUrl,
            username: draft.username,
            password: draft.password,
            rejectUnauthorized: draft.rejectUnauthorized,
          },
          { validateStatus: () => true }
        );
      } else {
        res = await API.post(`/api/connections/${draftId}/check-credentials`, {}, { validateStatus: () => true });
      }
      const data = res.data || {};
      if (res.status >= 200 && res.status < 300) {
        setDraftEmpsCheck(data);
      } else {
        setDraftEmpsCheck({ success: false, msg: data.msg || data.error || `HTTP ${res.status}` });
      }
    } catch (err) {
      setDraftEmpsCheck({ success: false, msg: err.message || 'Request failed' });
    } finally {
      setCheckDraftEmpsBusy(false);
    }
  }

  async function reload() {
    setLoading(true);
    setError('');
    try {
      const res = await API.get('/api/connections', { validateStatus: () => true });
      if (res.status === 503) {
        setMongoMissing(true);
        setMongoDetail(res.data || null);
        setConnections([]);
      } else if (res.status === 200) {
        setMongoMissing(false);
        setMongoDetail(null);
        const list = res.data.connections || [];
        setConnections(list);
        const active = list.find((c) => c.isActive);
        setActiveId(active ? active.id : null);
      } else if (isAuthRequired(res)) {
        setError('Session expired — please sign in again.');
        onSessionLost?.();
      } else {
        setError(res.data?.error || `Unexpected HTTP ${res.status}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  function startNew() {
    setDraftId('new');
    setDraft(EMPTY_DRAFT);
    setError('');
    setDraftEmpsCheck(null);
  }

  function startEdit(c) {
    setDraftId(c.id);
    setDraft({
      name: c.name,
      description: c.description || '',
      baseUrl: c.baseUrl,
      username: c.username,
      password: '', // never preload; empty means "keep existing"
      rejectUnauthorized: c.rejectUnauthorized !== false,
      aiSqlRunnerReportPath: c.aiSqlRunnerReportPath || '',
      aiSqlRunnerQueryParam: c.aiSqlRunnerQueryParam || '',
      aiSqlRunnerSqlEncoding: c.aiSqlRunnerSqlEncoding || '',
      fndAuditReportPath: c.fndAuditReportPath || '',
      fndAuditQueryParam: c.fndAuditQueryParam || '',
      fndAuditSqlEncoding: c.fndAuditSqlEncoding || '',
      auditPollSource: c.auditPollSource || 'hcm_sensitive',
      fusionDbEnabled: c.fusionDbEnabled === true,
      fusionDbUser: c.fusionDbUser || '',
      fusionDbPassword: '',
      fusionDbConnectString: c.fusionDbConnectString || '',
      fusionDbWalletPath: c.fusionDbWalletPath || '',
      environmentLabel: c.environmentLabel || '',
    });
    setError('');
    setDraftEmpsCheck(null);
  }

  function cancelDraft() {
    setDraftId(null);
    setDraft(EMPTY_DRAFT);
    setError('');
    setDraftEmpsCheck(null);
  }

  async function saveDraft() {
    setSavingDraft(true);
    setError('');
    try {
      if (draftId === 'new') {
        const res = await API.post('/api/connections', draft, { validateStatus: () => true });
        if (res.status >= 200 && res.status < 300) {
          cancelDraft();
          await reload();
        } else if (isAuthRequired(res)) {
          setError('Session expired — please sign in again.');
          onSessionLost?.();
        } else {
          setError(res.data?.error || `Save failed (HTTP ${res.status})`);
        }
      } else {
        const body = { ...draft };
        if (!body.password) delete body.password; // keep existing
        if (!body.fusionDbPassword) delete body.fusionDbPassword;
        const res = await API.put(`/api/connections/${draftId}`, body, {
          validateStatus: () => true,
        });
        if (res.status >= 200 && res.status < 300) {
          cancelDraft();
          await reload();
        } else if (isAuthRequired(res)) {
          setError('Session expired — please sign in again.');
          onSessionLost?.();
        } else {
          setError(res.data?.error || `Save failed (HTTP ${res.status})`);
        }
      }
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSavingDraft(false);
    }
  }

  async function deleteConnection(c) {
    if (!window.confirm(`Delete connection "${c.name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/api/connections/${c.id}`);
      await reload();
      onActiveChanged?.();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  }

  async function testConnection(c) {
    setTestingId(c.id);
    setTestResults((prev) => ({ ...prev, [c.id]: null }));
    try {
      const res = await API.post(
        `/api/connections/${c.id}/test`,
        {},
        { validateStatus: () => true }
      );
      setTestResults((prev) => ({ ...prev, [c.id]: res.data }));
      await reload();
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [c.id]: { success: false, message: err.message || 'Test failed' },
      }));
    } finally {
      setTestingId(null);
    }
  }

  async function activate(c) {
    try {
      await API.post(`/api/connections/${c.id}/activate`);
      setActiveId(c.id);
      await reload();
      onActiveChanged?.();
    } catch (err) {
      setError(err.message || 'Activate failed');
    }
  }

  async function deactivate() {
    try {
      await API.post('/api/connections/deactivate');
      setActiveId(null);
      await reload();
      onActiveChanged?.();
    } catch (err) {
      setError(err.message || 'Deactivate failed');
    }
  }

  const sorted = useMemo(
    () => [...connections].sort((a, b) => a.name.localeCompare(b.name)),
    [connections]
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-slate-400">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading connections…
      </div>
    );
  }

  if (mongoMissing) {
    const uriSet = mongoDetail?.mongoUriSet === true;
    const isLocal =
      typeof window !== 'undefined' &&
      /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-amber-700/50 bg-amber-950/40 p-5 text-sm text-amber-100">
          <div className="mb-2 flex items-center gap-2 font-semibold text-amber-50">
            <Database className="h-4 w-4" />
            MongoDB required for Connections
          </div>
          {mongoDetail?.error && (
            <p className="mb-3 rounded-lg bg-amber-950/60 px-3 py-2 text-amber-100/95">{mongoDetail.error}</p>
          )}
          {isLocal ? (
            <>
              <p className="text-amber-200/90">
                Saved Fusion pod credentials are stored encrypted in MongoDB. For local dev, add{' '}
                <code className="rounded bg-orca-raised px-1">MONGODB_URI</code> to the repo root{' '}
                <code className="rounded bg-orca-raised px-1">.env</code> (MongoDB Atlas free tier works), then
                restart the API (<code className="rounded bg-orca-raised px-1">npm run dev:reports</code>).
              </p>
              {uriSet ? (
                <p className="mt-3 text-xs text-amber-300/80">
                  <code className="rounded bg-orca-raised px-1">MONGODB_URI</code> is set but this API process is not
                  connected — stop any stale server on port 3010 and restart from the repo root. Check{' '}
                  <code className="rounded bg-orca-raised px-1">GET /api/health</code> →{' '}
                  <code className="rounded bg-orca-raised px-1">mongo: connected</code>.
                </p>
              ) : (
                <p className="mt-3 text-xs text-amber-300/80">
                  Quick local workaround without Mongo: set{' '}
                  <code className="rounded bg-orca-raised px-1">ORACLE_HCM_URL</code>,{' '}
                  <code className="rounded bg-orca-raised px-1">ORACLE_INTEGRATION_USER</code>, and{' '}
                  <code className="rounded bg-orca-raised px-1">ORACLE_INTEGRATION_PASSWORD</code> in{' '}
                  <code className="rounded bg-orca-raised px-1">.env</code> and restart — features use the env-based
                  connection until you save one here.
                </p>
              )}
            </>
          ) : (
            <>
              <p className="text-amber-200/90">
                Saved Fusion pod credentials are stored encrypted in MongoDB. Set{' '}
                <code className="rounded bg-orca-raised px-1">MONGODB_URI</code> on Heroku (Atlas free tier works),
                redeploy, then return here to add your tenant URL and integration user.
              </p>
              <p className="mt-3 text-xs text-amber-300/80">
                Heroku config vars (minimum): <code className="rounded bg-orca-raised px-1">MONGODB_URI</code>,{' '}
                <code className="rounded bg-orca-raised px-1">SESSION_SECRET</code>,{' '}
                <code className="rounded bg-orca-raised px-1">DEEPSEEK_API_KEY</code>. Do not put Fusion URLs or
                passwords in Heroku — configure them here per deployment.
              </p>
            </>
          )}
        </div>
        <ConnectionHealthPanel onSessionLost={onSessionLost} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ConnectionHealthPanel onSessionLost={onSessionLost} />
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-800">Customer Connections</h2>
        <div className="flex items-center gap-2">
          {activeId && (
            <button
              type="button"
              onClick={deactivate}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              title="Stop using a saved connection (fall back to .env)"
            >
              Use .env fallback
            </button>
          )}
          <button
            type="button"
            onClick={startNew}
            disabled={draftId !== null}
            className="flex items-center gap-1.5 rounded-lg bg-cyan-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-cyan-800 disabled:bg-slate-300"
          >
            <Plus className="h-4 w-4" />
            Add connection
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="break-words">{error}</div>
        </div>
      )}

      {draftId === 'new' && (
        <DraftRow
          draft={draft}
          setDraft={setDraft}
          saving={savingDraft}
          onSave={saveDraft}
          onCancel={cancelDraft}
          isNew
          onCheckEmps={checkDraftEmps}
          checkEmpsBusy={checkDraftEmpsBusy}
          checkEmpsResult={draftEmpsCheck}
        />
      )}

      {sorted.length === 0 && draftId !== 'new' ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          No saved connections yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Name</th>
                <th className="px-4 py-2 text-left font-semibold">Base URL</th>
                <th className="px-4 py-2 text-left font-semibold">User</th>
                <th className="px-4 py-2 text-left font-semibold">Last test</th>
                <th className="px-4 py-2 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((c) => {
                const isEditing = draftId === c.id;
                if (isEditing) {
                  return (
                    <tr key={c.id}>
                      <td colSpan={5} className="bg-cyan-50/40 px-4 py-3">
                        <DraftRow
                          draft={draft}
                          setDraft={setDraft}
                          saving={savingDraft}
                          onSave={saveDraft}
                          onCancel={cancelDraft}
                          isNew={false}
                          onCheckEmps={checkDraftEmps}
                          checkEmpsBusy={checkDraftEmpsBusy}
                          checkEmpsResult={draftEmpsCheck}
                        />
                      </td>
                    </tr>
                  );
                }
                const testResult = testResults[c.id];
                return (
                  <tr key={c.id} className={c.isActive ? 'bg-emerald-50/40' : ''}>
                    <td className="px-4 py-2.5 align-top">
                      <div className="flex items-center gap-2">
                        {c.isActive && (
                          <Star className="h-3.5 w-3.5 fill-emerald-500 text-emerald-500" title="Active" />
                        )}
                        <div>
                          <div className="font-medium text-slate-800">{c.name}</div>
                          {c.description && (
                            <div className="text-xs text-slate-500">{c.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="break-all font-mono text-xs text-slate-700">{c.baseUrl}</div>
                      {!c.rejectUnauthorized && (
                        <div className="text-[11px] text-amber-600">TLS check disabled</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top text-slate-700">{c.username}</td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="flex flex-col gap-1">
                        <StatusPill status={c.lastTestStatus} message={c.lastTestMessage} />
                        {c.lastTestAt && (
                          <div className="text-[11px] text-slate-400">
                            {new Date(c.lastTestAt).toLocaleString()}
                          </div>
                        )}
                        {testResult && testResult.message && (
                          <div className={`text-[11px] ${testResult.success ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {testResult.message}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => testConnection(c)}
                          disabled={testingId === c.id}
                          className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          title="Test this connection"
                        >
                          {testingId === c.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Plug className="h-3 w-3" />
                          )}
                          Test
                        </button>
                        {c.isActive ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                            <CheckCircle2 className="h-3 w-3" /> Active
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => activate(c)}
                            className="rounded-md bg-cyan-700 px-2.5 py-1 text-xs font-medium text-white hover:bg-cyan-800"
                          >
                            Use this
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => startEdit(c)}
                          disabled={draftId !== null}
                          className="rounded-md border border-slate-200 bg-white p-1 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                          title="Edit"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteConnection(c)}
                          className="rounded-md border border-rose-200 bg-white p-1 text-rose-600 hover:bg-rose-50"
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DraftRow({
  draft,
  setDraft,
  saving,
  onSave,
  onCancel,
  isNew,
  onCheckEmps,
  checkEmpsBusy,
  checkEmpsResult,
}) {
  function update(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  return (
    <div className="rounded-xl border border-cyan-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">
          {isNew ? 'New customer connection' : 'Edit connection'}
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            <X className="h-3 w-3" /> Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !draft.name || !draft.baseUrl || !draft.username || (isNew && !draft.password)}
            className="flex items-center gap-1 rounded-md bg-cyan-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-800 disabled:bg-slate-300"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            {isNew ? 'Create' : 'Save changes'}
          </button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" required>
          <input
            value={draft.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="e.g. ACME Production"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
        </Field>
        <Field label="Description (optional)">
          <input
            value={draft.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder="Free text shown in the badge"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
        </Field>
        <Field label="Environment (masking validation)">
          <select
            value={draft.environmentLabel}
            onChange={(e) => update('environmentLabel', e.target.value)}
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          >
            <option value="">Not set</option>
            <option value="non-production">Non-production</option>
            <option value="uat">UAT</option>
            <option value="dev">Dev</option>
            <option value="production">Production (validation warns)</option>
          </select>
        </Field>
        <Field label="Base URL" required>
          <input
            value={draft.baseUrl}
            onChange={(e) => update('baseUrl', e.target.value)}
            placeholder="https://acme.fa.us6.oraclecloud.com"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
        </Field>
        <Field label="Integration user" required>
          <input
            value={draft.username}
            onChange={(e) => update('username', e.target.value)}
            placeholder="INT_REPORTING"
            autoComplete="off"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
        </Field>
        <Field label={isNew ? 'Password (required)' : 'Password (leave blank to keep current)'}>
          <input
            type="password"
            value={draft.password}
            onChange={(e) => update('password', e.target.value)}
            placeholder={isNew ? '' : '••••••••'}
            autoComplete="new-password"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
        </Field>
        <Field label="TLS verification">
          <label className="flex items-center gap-2 px-1 py-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={!!draft.rejectUnauthorized}
              onChange={(e) => update('rejectUnauthorized', e.target.checked)}
              className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
            />
            Verify TLS certificate (recommended)
          </label>
        </Field>
        <div className="sm:col-span-2">
          <Field label="AI SQL Runner report path (optional)">
            <input
              value={draft.aiSqlRunnerReportPath}
              onChange={(e) => update('aiSqlRunnerReportPath', e.target.value)}
              placeholder="/Custom/SAAS Tool/V1/SAAS Tool Report.xdo"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            />
          </Field>
        </div>
        <Field label="SQL bind parameter (optional)">
          <input
            value={draft.aiSqlRunnerQueryParam}
            onChange={(e) => update('aiSqlRunnerQueryParam', e.target.value)}
            placeholder="query1 or p_sql"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
        </Field>
        <Field label="SQL encoding (optional)">
          <select
            value={draft.aiSqlRunnerSqlEncoding}
            onChange={(e) => update('aiSqlRunnerSqlEncoding', e.target.value)}
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          >
            <option value="">(auto)</option>
            <option value="base64">base64</option>
            <option value="plain">plain</option>
          </select>
        </Field>

        <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-3">
          <p className="text-xs font-semibold text-slate-700">FND audit BIP (optional — separate from SAAS Tool)</p>
          <Field label="FND audit report path">
            <input
              value={draft.fndAuditReportPath}
              onChange={(e) => update('fndAuditReportPath', e.target.value)}
              placeholder="/Custom/Security/FND Audit Report.xdo"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="FND SQL bind parameter (optional)">
              <input
                value={draft.fndAuditQueryParam}
                onChange={(e) => update('fndAuditQueryParam', e.target.value)}
                placeholder="query1 or p_sql"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-xs"
              />
            </Field>
            <Field label="FND SQL encoding (optional)">
              <select
                value={draft.fndAuditSqlEncoding}
                onChange={(e) => update('fndAuditSqlEncoding', e.target.value)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">(auto)</option>
                <option value="base64">base64</option>
                <option value="plain">plain</option>
              </select>
            </Field>
          </div>
          <Field label="Audit poll source (PER vs FND)">
            <select
              value={draft.auditPollSource || 'hcm_sensitive'}
              onChange={(e) => update('auditPollSource', e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="hcm_sensitive">HCM PER tables (default)</option>
              <option value="bip_fnd">FND BIP audit report</option>
              <option value="fusion_db">Fusion DB (ATP)</option>
            </select>
          </Field>
        </div>

        <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={!!draft.fusionDbEnabled}
              onChange={(e) => update('fusionDbEnabled', e.target.checked)}
              className="rounded border-slate-300 text-cyan-600"
            />
            Fusion DB read-only (FND audit via JDBC)
          </label>
          {draft.fusionDbEnabled && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="DB user">
                <input
                  value={draft.fusionDbUser}
                  onChange={(e) => update('fusionDbUser', e.target.value)}
                  placeholder="FUSION_READ"
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </Field>
              <Field label={isNew ? 'DB password' : 'DB password (blank = keep)'}>
                <input
                  type="password"
                  value={draft.fusionDbPassword}
                  onChange={(e) => update('fusionDbPassword', e.target.value)}
                  autoComplete="new-password"
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </Field>
              <Field label="Connect string (TNS or easy connect)">
                <input
                  value={draft.fusionDbConnectString}
                  onChange={(e) => update('fusionDbConnectString', e.target.value)}
                  placeholder="mydb_high"
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-xs"
                />
              </Field>
              <Field label="Wallet path (optional)">
                <input
                  value={draft.fusionDbWalletPath}
                  onChange={(e) => update('fusionDbWalletPath', e.target.value)}
                  placeholder="C:/wallet/ATP"
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-xs"
                />
              </Field>
            </div>
          )}
        </div>

      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        <button
          type="button"
          onClick={onCheckEmps}
          disabled={
            checkEmpsBusy ||
            !draft.baseUrl ||
            !draft.username ||
            (isNew && !draft.password)
          }
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          title="Verify HCM REST connection"
        >
          {checkEmpsBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserCheck className="h-3 w-3" />}
          Verify connection
        </button>
        {!isNew && !draft.password && (
          <span className="text-[11px] text-slate-500">Uses saved password if the field is empty.</span>
        )}
        {checkEmpsResult && (
          <span
            className={`text-xs ${checkEmpsResult.success ? 'text-emerald-700' : 'text-rose-700'}`}
          >
            {checkEmpsResult.success ? 'emps/1 OK.' : checkEmpsResult.msg || 'Check failed.'}
          </span>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-600">
        {label}
        {required && <span className="ml-0.5 text-rose-500">*</span>}
      </span>
      {children}
    </label>
  );
}
